/**
 * Formulário de uma apólice de saúde.
 *
 * Papel: preenche, valida e grava uma apólice — titular, operadora, plano,
 * carteirinha, CNS, as duas datas, a observação e a foto da carteirinha —,
 * servindo tanto à criação quanto à edição.
 *
 * Como usar: quem monta este componente passa `plano` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por apólice. Sem a `key`,
 * o React reaproveita a mesma instância ao trocar de registro e os campos
 * continuam com o que a pessoa via antes, porque `defaultValues` só é lido na
 * montagem.
 *
 * A foto só é gravada no "Salvar", e depois do plano: primeiro o plano, pelo
 * fluxo de sempre, e só então o envio da foto nova ou a exclusão marcada, que
 * precisam do identificador da apólice. Se o plano gravar e a foto falhar, a
 * caixa continua aberta com a mensagem da falha e passa a editar a apólice
 * gravada, guardada em `gravado`: um novo "Salvar" altera aquela apólice e
 * tenta a foto de novo, e não cria a apólice uma segunda vez.
 *
 * A carteirinha é campo de texto comum, sem máscara: o formato é da operadora —
 * `12345678 00 01 2` — e uma máscara nossa imporia uma pontuação que a próxima
 * operadora não usa. O CNS, esse sim, tem formato fixo de quinze dígitos, e
 * quem confere é `cnsOpcionalSchema`.
 */
import { type NovoPlanoSaude, type PlanoSaude, novoPlanoSaudeSchema } from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm, useWatch } from 'react-hook-form';

import { usePessoasFamilia } from '@/modules/cadastros-base';
import { ErroDaApi } from '@/shared/api';
import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import {
  useEnviarFotoDaCarteirinha,
  useExcluirFotoDaCarteirinha,
} from '../hooks/usar-foto-da-carteirinha';
import { useSalvarPlano } from '../hooks/usar-saude';
import {
  type EstadoDaFoto,
  FOTO_MANTIDA,
  PLANO_EM_BRANCO,
  type PlanoNoFormulario,
} from '../types';
import { CampoFotoDaCarteirinha } from './campo-foto-da-carteirinha';

interface FormularioPlanoProps {
  /** A apólice a editar, ou `null` para criar uma nova. */
  plano: PlanoSaude | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<PlanoNoFormulario> | undefined> = {
  pessoaId: 'pessoaId',
  operadora: 'operadora',
  plano: 'plano',
  carteirinha: 'carteirinha',
  cns: 'cns',
  ingresso: 'ingresso',
  validade: 'validade',
  observacao: 'observacao',
};

/**
 * Converte a apólice vinda da API no formato que o formulário guarda.
 *
 * @param plano - A apólice a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais, com `null` trocado por string vazia.
 */
function valoresIniciais(plano: PlanoSaude | null): PlanoNoFormulario {
  if (plano === null) return { ...PLANO_EM_BRANCO };

  return {
    pessoaId: plano.pessoa.id,
    operadora: plano.operadora,
    plano: plano.plano,
    carteirinha: plano.carteirinha,
    cns: plano.cns ?? '',
    ingresso: plano.ingresso ?? '',
    validade: plano.validade ?? '',
    observacao: plano.observacao ?? '',
  };
}

/**
 * A mensagem de uma falha da foto depois de o plano já estar gravado.
 *
 * @param erro - O que a mutação da foto rejeitou.
 * @param acao - `enviada` para o envio, `excluída` para a exclusão.
 * @returns A frase inteira, com a mensagem do servidor — a de `campos.foto`,
 *   quando a API apontou a imagem, e a geral no resto, inclusive a do 503.
 */
function mensagemDaFalhaDaFoto(erro: unknown, acao: 'enviada' | 'excluída'): string {
  const motivo =
    erro instanceof ErroDaApi
      ? (erro.campos?.foto?.[0] ?? erro.message)
      : 'a gravação falhou antes de chegar ao servidor.';
  return `O plano foi salvo, mas a foto não foi ${acao}: ${motivo}`;
}

/**
 * O formulário de criação e de edição de apólice.
 *
 * @param plano - A apólice a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioPlano({ plano, onSalvo, onCancelar }: FormularioPlanoProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [gravado, setGravado] = useState<PlanoSaude | null>(plano);
  const [foto, setFoto] = useState<EstadoDaFoto>(FOTO_MANTIDA);
  const pessoas = usePessoasFamilia();
  const salvar = useSalvarPlano();
  const enviarFoto = useEnviarFotoDaCarteirinha();
  const excluirFoto = useExcluirFotoDaCarteirinha();

  const form = useForm<PlanoNoFormulario, unknown, NovoPlanoSaude>({
    resolver: zodResolver(novoPlanoSaudeSchema),
    defaultValues: valoresIniciais(plano),
  });
  const pessoaId = useWatch({ control: form.control, name: 'pessoaId' });
  const nomeDoPlano = useWatch({ control: form.control, name: 'plano' });
  const titular = pessoas.data?.find((pessoa) => pessoa.id === pessoaId);

  const { errors } = form.formState;
  const enviando = salvar.isPending || enviarFoto.isPending || excluirFoto.isPending;

  /**
   * Grava a apólice, leva o erro da API para o campo que o causou e, com a
   * apólice gravada, envia ou exclui a foto.
   */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);

    let salvo: PlanoSaude;
    try {
      salvo = await salvar.mutateAsync({ id: gravado?.id ?? null, dados });
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
      return;
    }
    setGravado(salvo);

    try {
      if (foto.tipo === 'nova') {
        await enviarFoto.mutateAsync({ id: salvo.id, dados: { foto: foto.dataUrl } });
      } else if (foto.tipo === 'excluida') {
        await excluirFoto.mutateAsync(salvo.id);
      }
    } catch (erro) {
      setErroGeral(mensagemDaFalhaDaFoto(erro, foto.tipo === 'nova' ? 'enviada' : 'excluída'));
      return;
    }

    onSalvo();
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="plano-pessoa">Titular</Label>
          <Controller
            control={form.control}
            name="pessoaId"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={enviando || pessoas.isPending}
              >
                <SelectTrigger id="plano-pessoa" className="w-full">
                  <SelectValue
                    placeholder={pessoas.isPending ? 'Carregando…' : 'Escolha de quem é'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(pessoas.data ?? []).map((pessoa) => (
                    <SelectItem key={pessoa.id} value={pessoa.id}>
                      {pessoa.nomeCompleto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {pessoas.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhuma pessoa cadastrada ainda — cadastre em Cadastros-base antes.
            </p>
          )}
          {errors.pessoaId && (
            <p className="text-sm text-destructive" role="alert">
              {errors.pessoaId.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="plano-operadora">Operadora</Label>
          <Input
            id="plano-operadora"
            autoComplete="off"
            disabled={enviando}
            {...form.register('operadora')}
          />
          {errors.operadora && (
            <p className="text-sm text-destructive" role="alert">
              {errors.operadora.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="plano-nome">Plano</Label>
          <Input
            id="plano-nome"
            autoComplete="off"
            placeholder="Como está no contrato, com o código"
            disabled={enviando}
            {...form.register('plano')}
          />
          {errors.plano && (
            <p className="text-sm text-destructive" role="alert">
              {errors.plano.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="plano-carteirinha">Carteirinha</Label>
          <Input
            id="plano-carteirinha"
            autoComplete="off"
            placeholder="Exatamente como está impressa"
            disabled={enviando}
            {...form.register('carteirinha')}
          />
          {errors.carteirinha && (
            <p className="text-sm text-destructive" role="alert">
              {errors.carteirinha.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="plano-cns">CNS</Label>
          <Input
            id="plano-cns"
            autoComplete="off"
            inputMode="numeric"
            placeholder="Os 15 dígitos do Cartão Nacional de Saúde"
            disabled={enviando}
            {...form.register('cns')}
          />
          {errors.cns && (
            <p className="text-sm text-destructive" role="alert">
              {errors.cns.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="plano-ingresso">Ingresso</Label>
          <Controller
            control={form.control}
            name="ingresso"
            render={({ field }) => (
              <CampoMascarado
                id="plano-ingresso"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                placeholder="DD/MM/AAAA"
              />
            )}
          />
          {errors.ingresso && (
            <p className="text-sm text-destructive" role="alert">
              {errors.ingresso.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="plano-validade">Validade</Label>
          <Controller
            control={form.control}
            name="validade"
            render={({ field }) => (
              <CampoMascarado
                id="plano-validade"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                placeholder="DD/MM/AAAA"
              />
            )}
          />
          {errors.validade && (
            <p className="text-sm text-destructive" role="alert">
              {errors.validade.message}
            </p>
          )}
        </div>
        <p className="col-span-full -mt-3 text-xs text-muted-foreground">
          Depois que a validade passa, o plano aparece no aviso da tela inicial pedindo a foto
          da carteirinha nova.
        </p>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="plano-observacao">Observações</Label>
          <Textarea
            id="plano-observacao"
            rows={3}
            disabled={enviando}
            {...form.register('observacao')}
          />
          {errors.observacao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.observacao.message}
            </p>
          )}
        </div>

        <CampoFotoDaCarteirinha
          gravado={gravado}
          estado={foto}
          onMudar={setFoto}
          nomeCompleto={titular?.nomeCompleto ?? ''}
          plano={nomeDoPlano}
          desabilitado={enviando}
        />

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar plano'}
        </Button>
      </DialogFooter>
    </form>
  );
}
